import { useCallback, useRef } from "react";
import {
  useBulkUpdateUsers,
  useTransferUserRecords
} from "@/entities/users/hooks";
import { UserStatus } from "@/entities/users/types";
import { useResetCompaniesInfinite } from "@/pages/companies/hooks";
import { useResetFilteredDeals } from "@/pages/deals/hooks";
import { useResetLeadsInfinite } from "@/pages/leads/hooks";
import {
  buildTransferPayload,
  didTransferMoveRecords,
  getTransferFailures,
  TransferFailedError
} from "../helpers";
import { TransferData } from "./types";

/**
 * Hands the departing users' records over before flipping them to inactive.
 *
 * Order matters: a transfer that fails after the status flip would strand the records on a
 * user the admin can no longer select, which is the state this fixes. Failing first leaves
 * everyone active and the whole action repeatable — including after a partial transfer, since
 * already-moved rows no longer match the endpoint's `WHERE <column> IN UNNEST(fromIds)`.
 */
export const useUserDeactivation = () => {
  const transferUserRecords = useTransferUserRecords();
  const bulkUpdateUsers = useBulkUpdateUsers();

  const mutationsRef = useRef({ transferUserRecords, bulkUpdateUsers });
  mutationsRef.current = { transferUserRecords, bulkUpdateUsers };

  // The transferred columns belong to these lists. All three come from the same
  // createUseFilteredEntitiesInfinite factory, which hardcodes refetchOnMount: false with no
  // per-caller override, so without an explicit reset a list opened earlier in the session keeps
  // showing the departing user as owner.
  const resetCompaniesInfinite = useResetCompaniesInfinite();
  const resetFilteredDeals = useResetFilteredDeals();
  const resetLeadsInfinite = useResetLeadsInfinite();

  const resettersRef = useRef({
    resetCompaniesInfinite,
    resetFilteredDeals,
    resetLeadsInfinite
  });
  resettersRef.current = {
    resetCompaniesInfinite,
    resetFilteredDeals,
    resetLeadsInfinite
  };

  const deactivateUsers = useCallback(
    async (userIds: string[], transferData: TransferData | null) => {
      const mutations = mutationsRef.current;

      if (transferData) {
        const payload = buildTransferPayload(userIds, transferData);
        if (payload) {
          const result =
            await mutations.transferUserRecords.mutateAsync(payload);
          const failedFields = getTransferFailures(result);
          const movedAnyRecords = didTransferMoveRecords(result);

          // Reset before the throw as well: a partial transfer has already committed the fields
          // that succeeded, so those lists are stale whether or not the whole call succeeded.
          if (movedAnyRecords) {
            const resetters = resettersRef.current;
            resetters.resetCompaniesInfinite();
            resetters.resetFilteredDeals();
            resetters.resetLeadsInfinite();
          }

          if (failedFields.length > 0) {
            throw new TransferFailedError(failedFields, movedAnyRecords);
          }
        }
      }

      await mutations.bulkUpdateUsers.mutateAsync(
        userIds.map((id) => ({ id, status: UserStatus.INACTIVE }))
      );
    },
    []
  );

  return { deactivateUsers };
};
